import React, { useEffect, useState, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createServices } from "../../utils/apis";
import { useSnackbar } from "notistack";
import { fetchServices } from "../../utils/apis";

const ENUM_SERVICE = {
  PLACES_API_SERVICE: "PLACES_API_SERVICE",
  LIVE_TRACKING_API_SERVICE: "LIVE_TRACKING_API_SERVICE",
  MAP_API_SERVICE: "MAP_API_SERVICE",
  OTP_API_SERVICE: "OTP_API_SERVICE",
  DIRECTION_API_SERVICE: "DIRECTION_API_SERVICE",
  DIRECTION_V3_API_SERVICE: "DIRECTION_V3_API_SERVICE",
  DISTANCE_MATRIX_API_SERVICE: "DISTANCE_MATRIX_API_SERVICE",
  AREA_ZONE_API_SERVICE: "AREA_ZONE_API_SERVICE",
  LOCATION_DECODER_API_SERVICE: "LOCATION_DECODER_API_SERVICE",
};

export const ENUM_SERVICE_OPTIONS = [
  { value: ENUM_SERVICE.PLACES_API_SERVICE, label: "Places API Service" },
  {
    value: ENUM_SERVICE.LIVE_TRACKING_API_SERVICE,
    label: "Live Tracking API Service",
  },
  { value: ENUM_SERVICE.MAP_API_SERVICE, label: "Map API Service" },
  { value: ENUM_SERVICE.OTP_API_SERVICE, label: "OTP API Service" },
  { value: ENUM_SERVICE.DIRECTION_API_SERVICE, label: "Direction API Service" },
  {
    value: ENUM_SERVICE.DIRECTION_V3_API_SERVICE,
    label: "Direction V3 API Service",
  },

  {
    value: ENUM_SERVICE.DISTANCE_MATRIX_API_SERVICE,
    label: "Distance Matrix API Service",
  },
  {
    value: ENUM_SERVICE.AREA_ZONE_API_SERVICE,
    label: "Area Zone API Service",
  },
  {
    value: ENUM_SERVICE.LOCATION_DECODER_API_SERVICE,
    label: "Location Decoder API Service",
  },
];

const servicesInitialValues = {
  name: "",
  description: "",
  type: "",
};

const searchContainerStyles = {
  position: "relative",
  width: "100%",
  maxWidth: "420px",
};

const searchInputStyles = {
  width: "100%",
  padding: "12px 44px 12px 44px",
  fontSize: "14px",
  fontWeight: 400,
  color: "var(--ink)",
  backgroundColor: "var(--land-soft)",
  border: "1.5px solid var(--line-strong)",
  borderRadius: "999px",
  outline: "none",
  transition: "all 0.25s ease",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};

const searchInputFocusStyles = {
  ...searchInputStyles,
  borderColor: "var(--brand)",
  backgroundColor: "#fff",
  boxShadow: "0 0 0 4px var(--brand-ring)",
};

const searchIconStyles = {
  position: "absolute",
  left: "14px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--ink-3)",
  pointerEvents: "none",
  transition: "color 0.25s ease",
};

const clearBtnStyles = {
  position: "absolute",
  right: "10px",
  top: "50%",
  transform: "translateY(-50%)",
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "var(--ink-3)",
  transition: "all 0.2s ease",
};

const SearchIcon = ({ color = "var(--ink-3)" }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{ stroke: color }}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ClearIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HighlightText = ({ text = "", highlight = "" }) => {
  if (!highlight.trim()) return <>{text}</>;

  const regex = new RegExp(
    `(${highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = String(text).split(regex);

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark
            key={i}
            style={{
              backgroundColor: "var(--brand-tint-2)",
              color: "var(--brand-hover)",
              padding: "1px 2px",
              borderRadius: "3px",
              fontWeight: 600,
            }}
          >
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

const ServiceTableList = ({ services = [] }) => {
  const [search, setSearch] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const filteredServices = useMemo(() => {
    if (!search.trim()) return services;

    const query = search.toLowerCase().trim();
    return services.filter((item) => {
      const { name = "", description = "", type = "" } = item ?? {};
      return (
        name.toLowerCase().includes(query) ||
        type.toLowerCase().includes(query) ||
        (description && description.toLowerCase().includes(query))
      );
    });
  }, [services, search]);

  return (
    <div className="mt-5 pt-4">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "20px",
        }}
      >
        <p className="page-title mb-0" style={{ margin: 0 }}>
          Services List
        </p>
        <div style={searchContainerStyles}>
          <div style={searchIconStyles}>
            <SearchIcon color={isFocused ? "var(--brand)" : "var(--ink-3)"} />
          </div>
          <input
            id="services-search-input"
            type="text"
            placeholder="Search by name, type, or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={isFocused ? searchInputFocusStyles : searchInputStyles}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={clearBtnStyles}
              title="Clear search"
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--brand)";
                e.currentTarget.style.backgroundColor = "var(--brand-tint)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--ink-3)";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <ClearIcon />
            </button>
          )}
        </div>
      </div>

      {search.trim() && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "14px",
            padding: "5px 14px",
            backgroundColor:
              filteredServices.length > 0 ? "var(--brand-tint)" : "var(--jam-tint)",
            color: filteredServices.length > 0 ? "var(--brand)" : "var(--jam)",
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: 500,
          }}
        >
          <span style={{ fontWeight: 700 }}>{filteredServices.length}</span>
          {filteredServices.length === 1 ? "result" : "results"} for "
          <span style={{ fontWeight: 600 }}>{search.trim()}</span>"
        </div>
      )}

      {filteredServices.length ? (
        <div
          style={{
            borderRadius: "var(--r-md)",
            overflow: "hidden",
            border: "1px solid var(--line)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <table className="table mb-0 border-0" style={{ width: "100%", tableLayout: "fixed" }}>
            <thead>
              <tr
                style={{
                  backgroundColor: "var(--land)",
                  borderBottom: "2px solid var(--line)",
                }}
              >
                <th
                  className="user-header-item fw-600"
                  style={{
                    width: "30%",
                    padding: "14px 16px",
                    fontSize: "13px",
                    textTransform: "none",
                    letterSpacing: "0",
                    color: "var(--ink-3)",
                    textAlign: "left",
                  }}
                >
                  Service Name
                </th>
                <th
                  className="user-header-item fw-600"
                  colSpan={2}
                  style={{
                    width: "25%",
                    padding: "14px 16px",
                    fontSize: "13px",
                    textTransform: "none",
                    letterSpacing: "0",
                    color: "var(--ink-3)",
                    textAlign: "left",
                  }}
                >
                  Type
                </th>
                <th
                  className="user-header-item fw-600"
                  style={{
                    width: "45%",
                    padding: "14px 16px",
                    fontSize: "13px",
                    textTransform: "none",
                    letterSpacing: "0",
                    color: "var(--ink-3)",
                    textAlign: "left",
                  }}
                >
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map((item, index) => {
                const { _id: id, name, description, type } = item ?? {};

                return (
                  <tr
                    className="data-row-verticle-middle"
                    key={id}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#fff" : "var(--land-soft)",
                      transition: "background-color 0.15s ease",
                      borderBottom: "1px solid var(--line)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "var(--brand-tint)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor =
                        index % 2 === 0 ? "#fff" : "var(--land-soft)";
                    }}
                  >
                    <td
                      className="user-data-item data-item-captilize"
                      style={{ padding: "12px 16px", fontWeight: 500, textAlign: "left" }}
                    >
                      <HighlightText text={name} highlight={search} />
                    </td>
                    <td
                      className="user-data-item"
                      colSpan={2}
                      style={{ padding: "12px 16px", textAlign: "left" }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          backgroundColor: "var(--brand-tint)",
                          color: "var(--brand)",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                        }}
                      >
                        <HighlightText text={type} highlight={search} />
                      </span>
                    </td>
                    <td
                      className="user-data-item data-item-captilize"
                      style={{ 
                        padding: "12px 16px", 
                        color: "var(--ink-3)", 
                        textAlign: "left",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={description}
                    >
                      <HighlightText text={description} highlight={search} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 24px",
            backgroundColor: "var(--land-soft)",
            borderRadius: "var(--r-md)",
            border: "1px dashed var(--line-strong)",
          }}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#d3cbe4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginBottom: "12px" }}
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
          <p
            style={{
              margin: 0,
              fontSize: "15px",
              fontWeight: 500,
              color: "var(--ink-3)",
            }}
          >
            No matching services found
          </p>
          {search.trim() && (
            <button
              onClick={() => setSearch("")}
              style={{
                marginTop: "12px",
                padding: "6px 18px",
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--brand)",
                backgroundColor: "var(--brand-tint)",
                border: "1px solid var(--brand-tint-2)",
                borderRadius: "999px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "var(--brand-tint-2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "var(--brand-tint)";
              }}
            >
              Clear search
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Services = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [fetch, setFetch] = useState(true);
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (fetch) {
      (async function () {
        const result = await fetchServices(enqueueSnackbar);
        if (result.statusCode === 200) {
          setServices(result.data);
        }
      })();
    }
    return setFetch(false);
  }, [fetch, setServices, enqueueSnackbar]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const result = await createServices(values, enqueueSnackbar);

    if (result?.data?.statusCode === 201) {
      enqueueSnackbar("Service created Successfully", { variant: "success" });
      setFetch(true);
    }
    resetForm();
    setSubmitting(false);
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    description: Yup.string().nullable(),
    type: Yup.string().required(" Type is required"),
  });

  return (
    <>
      <div
        className="col-lg-10 col-xl-8 col-xxl-6 form-card"
      >
        <Formik
          initialValues={servicesInitialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <div className="row m-0 w-100 row-gap-3">
                <div className="col-12 col-md-8 grid-col-side-padding ">
                  <label
                    htmlFor="modifyLocationTitle"
                    className="form-label primary-form-label"
                  >
                    Name
                  </label>
                  <Field
                    type="text"
                    name="name"
                    className={`form-control primary-form-control ${
                      errors.name && touched.name ? "is-invalid" : ""
                    }`}
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>

                <div className="col-12 col-md-8 grid-col-side-padding ">
                  <label
                    htmlFor="modifyLocationCategory"
                    className="form-label primary-form-label"
                  >
                    Service Type
                  </label>
                  <Field
                    as="select"
                    name="type"
                    className={`form-select primary-focus primary-form-control ${
                      touched.type && errors.type ? "is-invalid" : ""
                    }`}
                  >
                    <option value="" disabled>
                      Service Type
                    </option>
                    {ENUM_SERVICE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="type"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>

                <div className="col-12 col-md-8 grid-col-side-padding ">
                  <label
                    htmlFor="modifyLocationLatitude"
                    className="form-label primary-form-label"
                  >
                    Description
                  </label>
                  <Field
                    component="textarea"
                    rows="3"
                    name="description"
                    className="form-control primary-form-control"
                  />
                </div>

                <div className="col-12 col-md-8 px-0 my-5">
                  <button
                    type="submit"
                    className="btn fribe-primary-btn modify-location-btn-submit w-100"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <ServiceTableList services={services} />
    </>
  );
};

export default Services;
